import { toIsoTimestamp } from './packages-wire.js';
import type { TopUpMethodKind, TopUpRecord, TopUpStatus } from './top-up.js';

/**
 * `get_trade` top-up rows (`type=CHARGE`) → the Wallet's top-up history (MYP-107).
 *
 * `get_trade` is the vendor's answer for wallet history (Q&A v2, question 11): `getalipaylist`,
 * which the ticket first named, is Alipay (China Payment) records only, and times out anyway.
 * Shapes here are from a live read of MP66184 on 2026-10-01: 553 top-ups, newest first.
 *
 * Read on purpose, and nothing else: the row also carries the FPX buyer's name and e-mail, staff
 * remarks (`remark1`, e.g. "IT Test 不需处理"), the operator and the uploaded slip's path. None of
 * that is the customer's to see here, so none of it is copied into the record.
 */

type Row = Readonly<Record<string, unknown>>;

function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** `"50.00"` → 5000. Undefined for anything that is not a number. */
function cents(value: unknown): number | undefined {
  const raw = text(value);
  if (raw === undefined || !/^-?\d+(\.\d+)?$/.test(raw)) return undefined;
  return Math.round(Number(raw) * 100);
}

/**
 * `END` 处理完成 → success; `INVALID` 无效 → incomplete (started, never paid). Those are the only
 * two values seen live. `CANCEL` → cancelled: the vendor says top-up records spell it that way
 * (orders use `CANCLE`), and a customer who cancelled was not refused. Anything else: a status
 * that says it failed is `failed`, the rest `pending` — never guessed into success.
 */
export function toTopUpStatus(status: unknown, statusEn?: unknown): TopUpStatus {
  const code = text(status)?.toUpperCase();
  if (code === 'END') return 'success';
  if (code === 'INVALID') return 'incomplete';
  if (code === 'CANCEL' || code === 'CANCELLED' || code === 'CANCELED') return 'cancelled';
  if (/fail/i.test(`${code ?? ''} ${text(statusEn) ?? ''}`)) return 'failed';
  return 'pending';
}

/** `bankname` values seen live (2026-10-01), upper-cased with spaces squeezed → their kind. */
const METHOD_KINDS: Readonly<Record<string, TopUpMethodKind>> = {
  'MOL PAY - ONLINE': 'fiuu',
  'MAYBANK - ONLINE': 'maybank2u',
  'EWALLET PAYMENT': 'ewallet',
};

/**
 * `bankname` → its kind, for the exact values seen live only (case and spacing aside). Anything
 * else is undefined and keeps the vendor's own text on screen: a new value is shown as sent rather
 * than guessed into a label that may be wrong.
 */
export function toTopUpMethodKind(bankname: unknown): TopUpMethodKind | undefined {
  const key = text(bankname)?.toUpperCase().replace(/\s+/g, ' ');
  return key === undefined ? undefined : METHOD_KINDS[key];
}

/** Every usable top-up row, in the order the vendor sent them (newest first). */
export function normaliseTopUpRecords(rows: readonly unknown[]): readonly TopUpRecord[] {
  const records: TopUpRecord[] = [];

  for (const raw of rows) {
    if (typeof raw !== 'object' || raw === null) continue;
    const row = raw as Row;

    const reference = text(row['rechargeno']) ?? text(row['tradeno']);
    const amountCents = cents(row['amount']);
    if (reference === undefined || amountCents === undefined) continue;

    records.push({
      id: text(row['id']) ?? reference,
      reference,
      method: text(row['bankname']),
      methodKind: toTopUpMethodKind(row['bankname']),
      // `submitdate` is always filled; `handledate` is empty on most top-ups.
      dateIso:
        toIsoTimestamp(row['submitdate']) ??
        toIsoTimestamp(row['chargedate']) ??
        toIsoTimestamp(row['handledate']) ??
        '',
      amountCents: Math.abs(amountCents),
      currency: text(row['currency']) ?? 'RM',
      status: toTopUpStatus(row['status'], row['status_en']),
    });
  }

  return records;
}

/** How many top-ups actually went through — the Wallet summary's "Top-ups" figure. */
export function completedTopUpCount(records: readonly TopUpRecord[]): number {
  return records.filter((record) => record.status === 'success').length;
}
